/**
 * The files chosen for upload, before and while they are sent (File Uploads,
 * multi-file): one row each, under the drop zone, with the facts the upload
 * dialog used to ask one file at a time - its class (guessed from its name),
 * kind, date and, for a statement, the collection account.
 *
 * Nothing here sends anything. Proceed sends the rows on one code: a single
 * file as INTAKE_UPLOAD, several (up to MAX_STAGED, the catalogue's
 * MAX_UPLOAD_FILES) as INTAKE_UPLOAD_BATCH, whose code is bound to every
 * file's facts.
 *
 * A row's status is the progress column's, and only these:
 *
 *   READY    waiting to be sent (or to be fixed: rowProblem)
 *   SENDING  its code is being asked for, or its bytes posted
 *   DONE     the ticket was issued and the bucket took the bytes
 *   ERROR    refused, before or after the code - said in `error`; sent again on the next Proceed
 *
 * The scan and its verdict are the Uploaded files list's to show, not this one's.
 */
import { MAX_UPLOAD_BYTES, guessUpload } from './uploadFile.js';
import { CLASS_GROUPS, CLASS_LABEL, dateFor, dateProblem, dateRule, kindOptions, takesLoadKind } from './uploadKinds.js';

/** How many files the list holds at a time. */
export const MAX_STAGED = 10;

export const ROW_STATUS = Object.freeze({ READY: 'READY', SENDING: 'SENDING', DONE: 'DONE', ERROR: 'ERROR' });

/**
 * The funds whose security holdings and fund facts are uploaded: GET
 * register/funds' funds, by fundId, all but a CLOSED one - a SUSPENDED fund
 * stops dealing (Rule 55), not holding securities.
 */
export const uploadFundIds = (funds) => (funds ?? []).filter((f) => f.status !== 'CLOSED').map((f) => f.fundId);

/**
 * The File category choice, grouped as the drop zone's own words group them:
 * a security holdings and a fund facts class for each fund given
 * (uploadFundIds). A Core data class with no kind this person may upload
 * (kindOptions - Unit prices, an initial load alone, without
 * core.intake.initial) and a group with no options - the funds not loaded
 * yet - are left out.
 *
 * @param {string[]} fundIds
 * @param {boolean} [canInitial]
 */
export function classOptions(fundIds, canInitial = true) {
  const each = (values) => values
    .filter((value) => !takesLoadKind(value) || kindOptions(canInitial, value).length > 0)
    .map((value) => ({ value, label: CLASS_LABEL[value] }));
  return CLASS_GROUPS.map((group) => ({
    label: group.label,
    options: group.fundPrefix ? fundIds.map((f) => ({ value: group.fundPrefix + f, label: f + ' ' + group.fundNoun })) : each(group.classes),
  })).filter((group) => group.options.length > 0);
}

/** The same file chosen twice: its name, length and last change. */
const sameFile = (a, b) => a.name === b.name && a.size === b.size && (a.lastModified ?? null) === (b.lastModified ?? null);

/** Why a chosen file is not added to the list, or null. */
function refusal(file) {
  if (!/\.csv$/i.test(file.name ?? '')) return 'only .csv files can be uploaded';
  if (!file.size) return 'it is empty';
  if (file.size > MAX_UPLOAD_BYTES) return 'it is over the 4 MB limit';
  return null;
}

/**
 * A row's class or kind changed (by hand, or by its name): the kind follows
 * the class (none for a NAV file or a statement; else the first kind
 * offered - Daily, or Unit prices' Initial load), the date follows the kind's rule (dateFor - the one
 * chosen, while the rule takes it), and only a statement keeps an account.
 * A row changed after it was refused is ready to be sent again.
 */
export function settleRow(row, changed, { canInitial, today }) {
  const next = { ...row, ...changed };
  if (!takesLoadKind(next.fileClass)) next.loadKind = null;
  else {
    const offered = kindOptions(canInitial, next.fileClass).map((o) => o.value);
    if (!offered.includes(next.loadKind)) next.loadKind = offered[0] ?? null;
  }
  next.businessDate = dateFor(dateRule(next.fileClass, next.loadKind).rule, next.businessDate, today);
  if (next.fileClass !== 'BANK_STATEMENT') next.accountId = null;
  if (next.status === ROW_STATUS.ERROR) {
    next.status = ROW_STATUS.READY;
    next.error = null;
  }
  return next;
}

/**
 * Add the files chosen (one drop or one pick) to the list. A file that is not
 * a CSV, is empty or too big, is already listed, or would pass MAX_STAGED is
 * left out and said why.
 *
 * @param {object[]} rows the list as it is
 * @param {{ uid?: string, name: string, size: number, lastModified?: number }[]} chosen File objects
 * @returns {{ rows: object[], skipped: { fileName: string, reason: string }[] }}
 */
export function stageFiles(rows, chosen, { canInitial, today }) {
  const next = [...rows];
  const skipped = [];
  for (const file of chosen) {
    const reason = refusal(file)
      ?? (next.some((r) => sameFile(r.file, file)) ? 'it is already in the list' : null)
      ?? (next.length >= MAX_STAGED ? 'the list holds ' + MAX_STAGED + ' files at a time' : null);
    if (reason) {
      skipped.push({ fileName: file.name, reason });
      continue;
    }
    const named = guessUpload(file.name);
    const row = {
      key: file.uid ?? file.name + ':' + file.size + ':' + (file.lastModified ?? ''),
      file,
      fileClass: null,
      loadKind: null,
      businessDate: today,
      accountId: null,
      status: ROW_STATUS.READY,
      error: null,
    };
    next.push(named ? settleRow(row, { fileClass: named.fileClass, loadKind: named.loadKind }, { canInitial, today }) : row);
  }
  return { rows: next, skipped };
}

/** Set one date on every row still to be sent: each row keeps it while its own rule takes it (dateFor). */
export function dateForAll(rows, businessDate, { canInitial, today }) {
  return rows.map((r) => (r.status === ROW_STATUS.DONE || r.status === ROW_STATUS.SENDING
    ? r
    : settleRow(r, { businessDate }, { canInitial, today })));
}

/**
 * What still stops a row being sent - the field and the words shown under
 * it - or null. The same checks prepareUpload makes, so Proceed is offered
 * only for rows it would send.
 *
 * @returns {{ field: 'fileClass'|'loadKind'|'businessDate'|'accountId', text: string } | null}
 */
export function rowProblem(row, today) {
  if (!row.fileClass) return { field: 'fileClass', text: 'Choose the file category' };
  if (takesLoadKind(row.fileClass) && row.loadKind !== 'DAILY' && row.loadKind !== 'INITIAL') {
    return { field: 'loadKind', text: 'Choose daily or initial load' };
  }
  const date = dateProblem(dateRule(row.fileClass, row.loadKind).rule, row.businessDate, today);
  if (date) return { field: 'businessDate', text: date };
  if (row.fileClass === 'BANK_STATEMENT' && !row.accountId) return { field: 'accountId', text: 'Choose the account the statement is for' };
  return null;
}

/** The rows Proceed sends: every one not yet sent, a refused one again. */
export const toSend = (rows) => rows.filter((r) => r.status === ROW_STATUS.READY || r.status === ROW_STATUS.ERROR);

/** The footer's counts, and whether Proceed is offered. */
export function listSummary(rows, today) {
  const pending = toSend(rows);
  const problems = pending.filter((r) => rowProblem(r, today)).length;
  return {
    total: rows.length,
    pending: pending.length,
    problems,
    done: rows.filter((r) => r.status === ROW_STATUS.DONE).length,
    failed: rows.filter((r) => r.status === ROW_STATUS.ERROR).length,
    canProceed: pending.length > 0 && problems === 0,
  };
}

/** A file's length for the list: "3.2 KB". */
export function sizeText(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
